import Promise from 'bluebird'
import { cloneDeep, chunk } from 'lodash'
import arrayDiff from 'src/utils/arrayDiff'
import { NotificationService } from 'src/services/NotificationService'
import { i18n } from '../../boot/i18n'
import { AuditService } from 'src/services/AuditService'
import {
  serialize,
  nextDueDate,
  AUDIT_SCHEMA_VERSION,
  isAuditSchemaOutdated
} from 'src/utils/audits'
import {
  normalizeCheckpoints,
  rejectExcludedCheckpoints
} from 'src/utils/checkpoints'
const { t } = i18n.global

const state = {
  untillDate: null,
  all: [],
  pendingAudits: [],
  isLoadingAudits: true,
  auditsInOtherScopes: [],
  currentOtherControl: {
    agency: { id: '', name: '' },
    residence: { id: '', name: '' }
  }
}

const getters = {
  all: (state) => {
    return state.all
  },
  todo: () => {
    return []
  },
  pendingAudits: (state) => {
    return state.pendingAudits
  },
  byPlace: (state, getters) => (placeId) =>
    getters.todo.filter((audit) => audit.place.id === parseInt(placeId)),
  isLoadingAudits: (state) => {
    return state.isLoadingAudits
  },
  auditsInOtherScopes: (state) => state.auditsInOtherScopes,
  currentOtherControl: (state) => {
    return state.currentOtherControl
  }
}

// NOTE: FEAT-4256 — un audit dont tous les checkpoints sont exclus n'a plus rien
// à contrôler. On le ferme localement (suppression du doc PouchDB) plutôt que de
// le laisser buter sur le garde-fou de saveAudits : le diff de reload se faisant
// par id, il serait sinon re-téléchargé à chaque passage du cron.
const closeFullyExcludedAudits = async (audits, commit) => {
  const fullyExcluded = audits.filter((audit) => audit.fullyExcluded)
  await Promise.each(fullyExcluded, async (audit) => {
    await AuditService.closeAudit({ id: `${audit.id}` })
    commit('CLOSE_AUDIT', { id: Number(audit.id) })
  })
  return audits.filter((audit) => !audit.fullyExcluded)
}

const actions = {
  async _populateAudits(_store, audits) {
    return audits.map((audit, index) => {
      const result = cloneDeep(audit)
      result.report = {
        id: null,
        checkpoints: [],
        'done-at': null
      }

      result.checklist.id = Number(audit.checklist.id)
      result.checklist.category.id = Number(audit.checklist.category.id)
      // FIXME:Audit.place.spots should be filtered in order to contain only spots with a category matching the audit's checklist category
      result.place.id = Number(audit.place.id)
      result.place.zone.id = Number(audit.place.zone.id)
      result.report.id = Number(audit.report?.id) || null
      // NOTE: RUN-2927 — la catégorie du checkpoint détermine la sous-catégorie
      // de l'anomalie et le filtrage des spots : on fige ici une forme canonique
      // NOTE: FEAT-4256 — checklist.checkpoints est le template complet, non
      // filtré : on en retire les checkpoints exclus sur cet audit
      const allCheckpoints = normalizeCheckpoints(audit.checklist.checkpoints)
      result.checkpoints = rejectExcludedCheckpoints(
        audit.checklist.checkpoints,
        audit['excluded-checkpoints']
      )
      // NOTE: FEAT-4256 — « plus rien à contrôler » (cas métier, l'audit se
      // ferme) doit rester distinct de « aucun checkpoint reçu » (données
      // corrompues, que le garde-fou de saveAudits refuse de persister)
      result.fullyExcluded =
        allCheckpoints.length > 0 && result.checkpoints.length === 0
      result.schemaVersion = AUDIT_SCHEMA_VERSION
      return result
    })
  },
  async reload({ commit, state, dispatch, rootGetters }) {
    const initialAudits = await AuditService.getAllAudits()
    const today = new Date().toISOString().split('T')[0]
    let doneTodayIds = []
    try {
      const doneToday = await this._vm.$DB.tmpReports().find({
        selector: { status: 'DONE' }
      })
      doneTodayIds = doneToday.docs
        .filter((r) => r.doneAt === today)
        .map((r) => String(r.audit.id))
    } catch (e) {}
    const activeIds = initialAudits
      .map((a) => a.id)
      .filter((id) => !doneTodayIds.includes(String(id)))
    commit('SET_ALL', activeIds)
    commit('IS_AUDITS_LOADING', true)
    return new Promise((resolve) => {
      let untillDate = this._vm.$helpers.stringToDate(nextDueDate())
      this._vm.$SowellProxy
        .reloadAudits(untillDate)
        .then(async (remoteAudits) => {
          commit('COMMIT_UNTILL_DATE', untillDate)
          const remoteAuditsIds = remoteAudits.map((a) => Number(a.id))
          const diff = arrayDiff(
            remoteAuditsIds,
            initialAudits.map((a) => Number(a.id))
          )

          // NOTE: clean DONE audits from a previous day — new day means new occurrence to do
          let doneReports = { docs: [] }
          try {
            doneReports = await this._vm.$DB.tmpReports().find({
              selector: {
                'audit.id': { $in: remoteAuditsIds },
                status: 'DONE'
              }
            })
          } catch (err) {
            console.warn('🚀 ~ file: index.js:185 ~ .then ~ err', err)
            doneReports = { docs: [] }
          }

          await Promise.map(doneReports.docs, async (report) => {
            if (report.doneAt === today) return
            await this._vm.$DB.tmpReports().remove(report)
            await AuditService.closeAudit({
              id: `${report.audit.id}`
            })
            commit('CLOSE_AUDIT', { id: `${report.audit.id}` })
          })

          await Promise.each(diff.missingLeft, async (auditId) => {
            await AuditService.closeAudit({
              id: `${auditId}`
            })
            commit('CLOSE_AUDIT', { id: `${auditId}` })
          })

          const chunksToSync = chunk(diff.missingRight, 20)

          // NOTE: RUN-2927 — reload ne re-télécharge que les audits absents en
          // local ; ceux persistés par une version antérieure (checkpoints sans
          // catégorie) doivent être ciblés explicitement, une seule fois
          const outdatedIds = initialAudits
            .filter((audit) => isAuditSchemaOutdated(audit))
            .map((audit) => Number(audit.id))
            .filter((id) => remoteAuditsIds.includes(id))

          if (chunksToSync.length) {
            await Promise.all(
              chunksToSync.map(async (chunk) => {
                try {
                  const response =
                    await this._vm.$SowellProxy.loadAuditsByChunks(chunk)
                  const populated = await dispatch(
                    '_populateAudits',
                    response.data
                  )
                  const audits = await closeFullyExcludedAudits(
                    populated,
                    commit
                  )
                  await AuditService.saveAudits(serialize(audits))
                  commit(
                    'SAVE_AUDITS',
                    audits.map((a) => a.id)
                  )
                } catch (error) {
                  console.error('err reload', error)
                  await AuditService.resetAllAudits(initialAudits)
                  commit(
                    'SET_ALL',
                    initialAudits.map((a) => a.id)
                  )
                  return resolve()
                }
              })
            )
          }

          // NOTE: bloc séparé du précédent à dessein — un échec du
          // rafraîchissement de schéma ne doit pas déclencher le
          // resetAllAudits ci-dessus : au pire l'audit sera repris au prochain
          // reload, ses checkpoints restant lisibles en attendant
          await Promise.all(
            chunk(outdatedIds, 20).map(async (ids) => {
              try {
                const response =
                  await this._vm.$SowellProxy.loadAuditsByChunks(ids)
                const populated = await dispatch(
                  '_populateAudits',
                  response.data
                )
                const audits = await closeFullyExcludedAudits(populated, commit)
                await AuditService.saveAudits(serialize(audits))
              } catch (error) {
                console.warn('reload: rafraîchissement du schéma échoué', error)
              }
            })
          )
          return resolve()
        })
        .catch((err) => {
          console.error('err reload', err)
          return resolve() // NOTE: it's ok if we can't reload, we keep the current audits list
        })
        .finally(() => {
          commit('IS_AUDITS_LOADING', false)
          this._vm.$DB
            .tmpReports()
            .find({
              selector: {
                status: { $in: ['PENDING', 'IN_PROGRESS', 'FAILED', 'DONE'] }
              }
            })
            .then(({ docs }) => {
              commit(
                'RESET_PENDING_AUDIT',
                docs.map((doc) => doc.audit.id)
              )
            })
        })
    })
  },
  reloadAudit({ commit, dispatch }, auditId) {
    return new Promise((resolve, reject) => {
      // garde-fou si la requête ne se termine jamais (offline)
      const timeout = setTimeout(() => {
        reject(new Error('reloadAudit timeout'))
      }, 30000)
      this._vm.$SowellProxy
        .loadAudit(auditId)
        .then(async (response) => {
          const populated = await dispatch('_populateAudits', [response.data])
          const audits = await closeFullyExcludedAudits(populated, commit)
          await AuditService.saveAudits(serialize(audits))
          return resolve(response.data)
        })
        .catch((err) => {
          return reject(err)
        })
        .finally(() => clearTimeout(timeout))
    })
  },
  loadAuditsInOtherScopes({ commit }, place) {
    return new Promise((resolve, reject) => {
      commit('IS_AUDITS_LOADING', true)
      this._vm.$SowellProxy
        .loadPlaceAudits(place.id)
        .then(async (response) => {
          if (response.data.length === 0) {
            return resolve([])
          } else {
            let audits = response.data
            let results = []
            for (const auditIndex in audits) {
              let audit = { ...audits[auditIndex] }
              // convert id to integer
              audit.id = `${audit.id}`
              audit.report = {
                id: null,
                checkpoints: [],
                'done-at': null
              }
              // convert id to integer
              audit.checklist.id = Number(audit.checklist.id)
              audit.checklist.category.id = Number(audit.checklist.category.id)
              audit.place.id = Number(audit.place.id)
              audit.place.zone.id = Number(audit.place.zone.id)
              audit.report.id = Number(audit.report.id)
              // NOTE: FEAT-4256 — même croisement que _populateAudits, que
              // cette action duplique historiquement
              audit.checkpoints = rejectExcludedCheckpoints(
                audit.checklist.checkpoints,
                audit['excluded-checkpoints']
              )
              audit.schemaVersion = AUDIT_SCHEMA_VERSION
              // NOTE: FEAT-4256 — un audit sans checkpoint à contrôler ne doit
              // pas être proposé : soit tout est exclu, soit l'API n'a rien
              // renvoyé, et dans les deux cas Report.vue ne saurait qu'en faire
              // (« Audit without checkpoint »). Ces audits ne sont pas persistés
              // en local, il n'y a donc rien à fermer ici.
              if (!audit.checkpoints.length) continue
              results = [...results, audit]
            }

            commit('ADD_AUDITS_FROM_OTHER_SCOPE', results)
            return resolve(results)
          }
        })
        .catch((error) => {
          console.error(
            '🚀 ~ file: index.js:283 ~ returnnewPromise ~ error:',
            error
          )
          this.$q.loading.hide()
          return reject('ERROR LOADING AUDITS')
        })
        .finally(() => {
          commit('IS_AUDITS_LOADING', false)
        })
    })
  },
  close({ commit }, payload) {
    AuditService.closeAudit(payload)
    commit('CLOSE_AUDIT', payload)
  },
  reset({ commit }, payload) {
    commit('RESET', payload)
    commit('RESET_AUDITS_IN_OTHER_SCOPES', payload)
  },
  resetPendingAudit({ commit }, audits) {
    commit('RESET_PENDING_AUDIT', audits)
  },
  addPendingAudit({ commit }, audit) {
    commit('REMOVE_PENDING_AUDIT', audit)
    commit('ADD_PENDING_AUDIT', audit)
  },
  removePendingAudit({ commit }, audit) {
    commit('REMOVE_PENDING_AUDIT', audit)
  },
  async createNotification(_state, force) {
    // TODO: afficher le message de notification correct
    await NotificationService.scheduleNotification(
      {
        title: t('notification.scheduleNotification.title'),
        body: t('notification.scheduleNotification.body')
      },
      force
    )
  }
}

const mutations = {
  SET_ALL(state, payload) {
    state.all = payload
  },
  SAVE_AUDITS(state, payload) {
    state.all = [...state.all, ...payload]
  },
  COMMIT_UNTILL_DATE(state, payload) {
    state.untillDate = payload
  },
  RESET(state) {
    state.untillDate = null
    state.all = []
    state.pendingAudits = []
    state.isLoadingAudits = true
  },
  CLOSE_AUDIT(state, payload) {
    state.all = state.all.filter((audit) => audit !== payload.id)
  },
  IS_AUDITS_LOADING(state, value) {
    state.isLoadingAudits = value
  },
  RESET_AUDITS_IN_OTHER_SCOPES(state) {
    state.auditsInOtherScopes = []
  },
  ADD_AUDITS_FROM_OTHER_SCOPE(state, audits) {
    state.auditsInOtherScopes = audits.map((payload) => serialize(payload))
  },
  RESET_CURRENT_OTHER_CONTROL(state) {
    state.currentOtherControl = {
      agency: { id: '', name: '' },
      residence: { id: '', name: '' }
    }
  },
  SAVE_SELECTED_RESIDENCE(state, { residence, agency }) {
    state.currentOtherControl = {
      agency,
      residence
    }
  },
  RESET_PENDING_AUDIT(state, audits) {
    state.pendingAudits = audits
  },
  ADD_PENDING_AUDIT(state, audit) {
    state.pendingAudits = [...state.pendingAudits, audit]
  },
  REMOVE_PENDING_AUDIT(state, audit) {
    state.pendingAudits = state.pendingAudits.filter((a) => a !== audit)
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
}
